import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAdminFetchApi from '../../utils/adminFetchApi';

const GATEWAY_OPTIONS = [
  { value: 'stripe', label: 'Stripe' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'razorpay', label: 'Razorpay' },
  { value: 'toybox', label: 'Toybox' },
];

export default function AdminPaymentLinks() {
  const api = useAdminFetchApi();
  const [links, setLinks] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Pagination (best-effort: fallback to no-params if server doesn't support)
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Upsert form state
  const [form, setForm] = useState({
    payment_link: '',
    channel: 'global',
    gateway: 'stripe',
    active: true,
    button_code: '',
  });

  // Inline edit drafts keyed by id
  const [drafts, setDrafts] = useState({}); // { [id]: { payment_link?, channel?, gateway? } }
  const [savingIds, setSavingIds] = useState(new Set());

  const clearMessages = () => { setError(''); setInfo(''); };

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    clearMessages();
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    try {
      const withParams = await api.get(`/admin/payment_links?${params.toString()}`);
      const payload = withParams?.data || withParams;
      const list = Array.isArray(payload?.payment_links) ? payload.payment_links : (Array.isArray(payload) ? payload : (Array.isArray(withParams?.payment_links) ? withParams.payment_links : []));
      setLinks(list);
      const t = (payload?.total ?? withParams?.total);
      if (typeof t === 'number') setTotal(t);
    } catch (e) {
      // Retry without params if server rejects limit/offset
      try {
        const data = await api.get('/admin/payment_links');
        const payload = data?.data || data;
        const list = Array.isArray(payload?.payment_links) ? payload.payment_links : (Array.isArray(payload) ? payload : (Array.isArray(data?.payment_links) ? data.payment_links : []));
        setLinks(list);
        const t = (payload?.total ?? data?.total);
        if (typeof t === 'number') setTotal(t);
      } catch (err) {
        setError(err.message || 'Failed to load payment links');
      }
    } finally {
      setLoading(false);
    }
  }, [api, limit, offset]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const canPrev = offset > 0;
  const canNext = useMemo(() => {
    if (total == null) return links.length === limit; // heuristic if total unknown
    return offset + limit < total;
  }, [offset, limit, total, links.length]);

  const prevPage = () => setOffset(prev => Math.max(0, prev - limit));
  const nextPage = () => setOffset(prev => prev + limit);

  const onChangeForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const submitUpsert = async (e) => {
    e?.preventDefault?.();
    clearMessages();
    const payload = {
      payment_link: (form.payment_link || '').trim(),
      channel: (form.channel || 'global').trim() || 'global',
      gateway: (form.gateway || 'stripe').trim() || 'stripe',
      active: !!form.active,
      button_code: (() => {
        const v = (form.button_code ?? '').toString();
        const t = v.trim();
        return t === '' ? null : v; // preserve whitespace/newlines if provided, else null
      })(),
    };
    if (!payload.payment_link) {
      setError('Payment link is required');
      return;
    }
    try {
      const resp = await api.post('/admin/payment_links', payload);
  setInfo(resp?.message || 'Payment link saved successfully');
  setForm({ payment_link: '', channel: payload.channel, gateway: payload.gateway, active: true, button_code: '' });
      // refresh list
      fetchLinks();
    } catch (e) {
      setError(e.message || 'Failed to save payment link');
    }
  };

  const getDraft = (id) => drafts[id] || {};
  const setDraft = (id, changes) => setDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...changes } }));

  const commitInline = async (rowId, changes) => {
    if (!rowId) return;
    // optimistic update
    setLinks(prev => prev.map(r => (r.id === rowId ? { ...r, ...changes } : r)));
    setSavingIds(prev => { const n = new Set(prev); n.add(rowId); return n; });
    try {
      await api.put(`/admin/payment_links/${rowId}`, changes);
    } catch (e) {
      setError(e.message || 'Update failed');
      // reload row to be safe
      try {
        const one = await api.get(`/admin/payment_links/${rowId}`);
        const row = one?.data || one;
        setLinks(prev => prev.map(r => (r.id === rowId ? row : r)));
      } catch (_) {}
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(rowId); return n; });
      setDrafts(prev => { const n = { ...prev }; delete n[rowId]; return n; });
    }
  };

  const renderCell = (row, key) => {
    const saving = savingIds.has(row.id);
    if (key === 'active') {
      const checked = !!row.active;
      return (
        <input type="checkbox" checked={checked} disabled={saving} onChange={(e)=>commitInline(row.id, { active: e.target.checked })} />
      );
    }
    if (key === 'payment_link') {
      const d = getDraft(row.id);
      const val = d.payment_link != null ? d.payment_link : (row.payment_link || '');
      return (
        <input
          type="text"
          placeholder="https://..."
          value={val}
          disabled={saving}
          onChange={(e)=>setDraft(row.id, { payment_link: e.target.value })}
          onBlur={()=>commitInline(row.id, { payment_link: val })}
          onKeyDown={(e)=>{ if (e.key==='Enter') commitInline(row.id, { payment_link: val }); if (e.key==='Escape') setDrafts(prev=>{ const n={...prev}; delete n[row.id]; return n; }); }}
          style={{ width: 360 }}
        />
      );
    }
    if (key === 'gateway') {
      const d = getDraft(row.id);
      const val = d.gateway != null ? d.gateway : (row.gateway || 'stripe');
      return (
        <select value={val} disabled={saving} onChange={(e)=>commitInline(row.id, { gateway: e.target.value })}>
          {GATEWAY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    }
    if (key === 'channel') {
      const d = getDraft(row.id);
      const val = d.channel != null ? d.channel : (row.channel || 'global');
      return (
        <input
          type="text"
          placeholder="global | iraq | malaysia"
          value={val}
          disabled={saving}
          onChange={(e)=>setDraft(row.id, { channel: e.target.value })}
          onBlur={()=>commitInline(row.id, { channel: val })}
          onKeyDown={(e)=>{ if (e.key==='Enter') commitInline(row.id, { channel: val }); if (e.key==='Escape') setDrafts(prev=>{ const n={...prev}; delete n[row.id]; return n; }); }}
          style={{ width: 160 }}
        />
      );
    }
    if (key === 'button_code') {
      const d = getDraft(row.id);
      const val = d.button_code != null ? d.button_code : (row.button_code || '');
      return (
        <textarea
          placeholder="HTML snippet or button identifier"
          value={val}
          disabled={saving}
          onChange={(e)=>setDraft(row.id, { button_code: e.target.value })}
          onBlur={()=>{
            const t = (val ?? '').toString().trim();
            commitInline(row.id, { button_code: t === '' ? null : val });
          }}
          onKeyDown={(e)=>{
            if (e.key==='Enter' && !e.shiftKey) {
              e.preventDefault();
              const t = (val ?? '').toString().trim();
              commitInline(row.id, { button_code: t === '' ? null : val });
            }
            if (e.key==='Escape') setDrafts(prev=>{ const n={...prev}; delete n[row.id]; return n; });
          }}
          rows={2}
          style={{ width: 360, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \'Liberation Mono\', \'Courier New\', monospace' }}
        />
      );
    }
    if (key === 'created_at' || key === 'updated_at') {
      const v = row[key];
      if (!v) return '';
      const d = new Date(v);
      return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
    }
    return row[key] ?? '';
  };

  const columns = useMemo(() => ['id','payment_link','gateway','channel','active','button_code','created_at','updated_at'], []);

  return (
    <div className="admin-section">
      <div className="admin-toolbar">
        <div className="field">
          <label>Page size</label>
          <select value={limit} onChange={(e)=>{ setLimit(parseInt(e.target.value,10)); setOffset(0); }}>
            {[25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="spacer" />
        <div className="pagination">
          <button onClick={prevPage} disabled={!canPrev || loading}>Prev</button>
          <span className="page-hint">offset {offset}{total!=null ? ` / ${total}` : ''}</span>
          <button onClick={nextPage} disabled={!canNext || loading}>Next</button>
        </div>
        <button onClick={fetchLinks} disabled={loading} className="submit-button" style={{width:'auto'}}>Refresh</button>
      </div>

      {error && <div className="error-message" style={{marginBottom:12}}>{error}</div>}
      {info && <div className="error-message" style={{marginBottom:12, background:'#12321e', color:'#c3f8ca', borderColor:'#14532d'}}>{info}</div>}

      <div className="panel" style={{marginBottom:12}}>
        <h3 style={{marginTop:0}}>Create or update payment link</h3>
        <form onSubmit={submitUpsert} className="grid-2">
          <div className="field span-2">
            <label>Payment link (URL)</label>
            <input type="text" value={form.payment_link} onChange={(e)=>onChangeForm('payment_link', e.target.value)} placeholder="https://checkout.stripe.com/..." />
          </div>
          <div className="field">
            <label>Channel</label>
            <input type="text" value={form.channel} onChange={(e)=>onChangeForm('channel', e.target.value)} placeholder="global | iraq | malaysia" />
          </div>
          <div className="field">
            <label>Gateway</label>
            <select value={form.gateway} onChange={(e)=>onChangeForm('gateway', e.target.value)}>
              {GATEWAY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div className="field inline">
            <label style={{marginRight:8}}>Active</label>
            <input type="checkbox" checked={!!form.active} onChange={(e)=>onChangeForm('active', e.target.checked)} />
          </div>
          <div className="field span-2">
            <label>Button code (optional)</label>
            <textarea
              value={form.button_code}
              onChange={(e)=>onChangeForm('button_code', e.target.value)}
              placeholder="HTML snippet or button identifier"
              rows={3}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \'Liberation Mono\', \'Courier New\', monospace' }}
            />
          </div>
          <div style={{display:'flex', justifyContent:'flex-end'}} className="span-2">
            <button type="submit" className="button primary">Save payment link</button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="loading-row">Loading payment links…</div>
      ) : (
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {columns.map(col => <th key={col}>{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {links.length === 0 ? (
                <tr><td colSpan={columns.length} style={{textAlign:'center', color:'#94a3b8'}}>No payment links</td></tr>
              ) : links.map((row) => (
                <tr key={row.id}>
                  {columns.map(col => (
                    <td key={col}>{renderCell(row, col)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
